import { createContext, useCallback, useContext, useEffect, useId, useState } from "react";
import { api, errMsg } from "@/lib/api";

const BrandContext = createContext({ clients: [], loading: true, error: "", refresh: async () => {} });
export const useClientBrands = () => useContext(BrandContext);
const titleFor = (c) => c?.brand_name || c?.company || c?.name || "";
const subFor = (c) => [c?.name, c?.company].filter(Boolean).join(" · ");

export const ClientBrandProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/client-brands"); setClients(data); setError(""); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return <BrandContext.Provider value={{ clients, loading, error, refresh }}>{children}</BrandContext.Provider>;
};

export const ClientLogo = ({ client, clientId, name, size = 32, testId }) => {
  const { clients } = useClientBrands();
  const id = useId();
  const c = clients.find((item) => item.id === (clientId || client?.id)) || client;
  const title = titleFor(c) || name || "Client";
  const src = c?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${c.logo_url}` : "";
  const [failed, setFailed] = useState("");
  const initials = title.trim().split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return <span className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-600 align-middle font-semibold normal-case tracking-normal" style={{ width: size, height: size, fontSize: size * 0.32 }} data-testid={testId || `client-logo-${id}`}>
    {src && failed !== src ? <img src={src} alt={`${title} logo`} className="h-full w-full object-contain p-1" onError={() => setFailed(src)} data-testid={`${testId || `client-logo-${id}`}-image`} /> : <span aria-label={`${title} initials`}>{initials}</span>}
  </span>;
};

export const ClientIdentity = ({ client, clientId, name, subtitle, size = 28, compact = false, className = "", testId }) => {
  const { clients } = useClientBrands();
  const id = useId();
  const c = clients.find((item) => item.id === (clientId || client?.id)) || client;
  const title = titleFor(c) || name || "Unknown client";
  const sub = subtitle ?? subFor(c);
  const tid = testId || `client-identity-${id}`;
  return <span className={`inline-flex items-center gap-2.5 min-w-0 max-w-full align-middle ${className}`} data-testid={tid}>
    <ClientLogo client={c} clientId={clientId} name={title} size={size} testId={`${tid}-logo`} />
    <span className="min-w-0"><span className="block truncate font-medium" data-testid={`${tid}-name`}>{title}</span>{!compact && sub && <span className="block truncate text-xs font-normal text-slate-600 normal-case tracking-normal" data-testid={`${tid}-subtitle`}>{sub}</span>}</span>
  </span>;
};
